import { Fragment, useEffect, useState } from "react";
import { Card, Form, Button, Modal } from "react-bootstrap";
import { Formik, FieldArray } from "formik";
import { useLocation, useNavigate } from "react-router-dom";
import RenderContent from "./RenderContent";
import AddLecture from "./AddLecture";
import { createCourseDetails } from "ServerAPI/serverAPI";
import { CircularProgress } from "@mui/material";
import { useCookies } from "react-cookie";

import React from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { LucideGripVertical } from "lucide-react";

const AddLectureModal = ({ show, handleClose, addLecture }) => {
  const [lectureTitle, setLectureTitle] = useState("");
  const navigate = useNavigate();

  const handleAddLecture = () => {
    if (lectureTitle.trim() !== "") {
      navigate("/add-lecture");
    }
  };

  return (
    <Modal show={show} onHide={handleClose} backdrop="static" keyboard={false}>
      <Modal.Header closeButton>
        <Modal.Title>Add New Lecture</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Control
            type="text"
            placeholder="Add new lecture"
            value={lectureTitle}
            onChange={(e) => setLectureTitle(e.target.value)}
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={handleAddLecture}>
          Add New Lecture
        </Button>
        <Button variant="outline-secondary" onClick={handleClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const AddSectionModal = ({
  show,
  handleClose,
  addSection,
  editMode,
  updateEditMode,
  setStep,
  isLoading,
  activeSection,
  allSections,
  setAllSections,
  sectionTitle,
  setSectionTitle,
}) => {
  const [sectionTitleErr, setSectionTitleErr] = useState(null);
  const handleAddSection = () => {
    if (sectionTitle.trim() !== "") {
      setSectionTitleErr(null);
      addSection();
    } else {
      setSectionTitleErr("Section title is required");
    }
  };

  const updateSectionName = () => {
    const updatedSections = [...allSections];
    updatedSections.forEach((section, index) => {
      if (section.sectionId === activeSection?.sectionId) {
        updatedSections[index].sectionTitle = sectionTitle;
      }
    });
    setAllSections(updatedSections);
    setSectionTitle("");
    updateEditMode(false);
    setStep(3);
    handleClose();
  };
  useEffect(() => {
    if (editMode) setSectionTitle(activeSection.sectionTitle);
  }, [activeSection]);
  return (
    <Modal show={show} onHide={handleClose} backdrop="static" keyboard={false}>
      <Modal.Header closeButton>
        {editMode ? (
          <>
            <Modal.Title>Update The Section</Modal.Title>
          </>
        ) : (
          <>
            <Modal.Title>Add New Section</Modal.Title>
          </>
        )}
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Control
            type="text"
            placeholder="Add new section"
            value={sectionTitle}
            onChange={(e) => setSectionTitle(e.target.value)}
            className="text-dark"
          />
          <div className="text-danger mt-2">{sectionTitleErr}</div>
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        {editMode ? (
          <>
            <Button variant="primary" onClick={updateSectionName}>
              Edit Section
            </Button>
          </>
        ) : (
          <>
            {isLoading ? (
              <CircularProgress />
            ) : (
              <Button variant="primary" onClick={handleAddSection}>
                Save
              </Button>
            )}
          </>
        )}

        <Button variant="outline-secondary" onClick={handleClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export function SortableItem(props) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: props.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {props.children}
    </div>
  );
}

const Curriculum = (props) => {
  const {
    next,
    previous,
    editMode,
    updateEditMode,
    setStep,
    allFiles,
    setAllFiles,
    courseDetails,
    setCourseDetails,
    courseId,
    setCourseId,
    allSections,
    setAllSections,
    allContent,
    setAllContent,
    activeSection,
    setActiveSection,
    activeContent,
    setActiveContent,
    activeQuiz,
    setActiveQuiz,
    showContentPage,
    setShowContentPage,
    setEditMode,
    resetActiveQuiz,
    savedCourse,
    editSavedCourse,
  } = props;
  const [showLectureModal, setShowLectureModal] = useState(false);
  const [showSectionModal, setShowSectionModal] = useState(false);
  const [curriculumError, setCurriculumError] = useState("");
  const [activeSectionIndex, setActiveSectionIndex] = useState(null);
  const [sectionTitle, setSectionTitle] = useState("");
  const [sectionId, setSectionId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [cookies, setCookies] = useCookies();

  // * Drag and Drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 10 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  function handleDragEnd(event, currentSection, currentSectionIndex) {
    const active = event?.active?.data?.current?.sortable?.index;
    const over = event?.over?.data?.current?.sortable?.index;

    // return allSections;
    if (active !== over) {
      setAllSections((sections) => {
        const newContent = arrayMove(currentSection?.content, active, over);

        const updatedContent = newContent.map((content, index) => {
          return {
            ...content,
            contentNumber: index + 1,
          };
        });

        return sections.map((section, index) => {
          if (index === currentSectionIndex) {
            return {
              ...currentSection,
              content: updatedContent,
            };
          } else {
            return section;
          }
        });
      });
    }
  }

  function handleSectionDragEnd(event) {
    const active = event?.active?.data?.current?.sortable?.index;
    const over = event?.over?.data?.current?.sortable?.index;

    console.log("active", active, "over", over);

    if (active !== over) {
      return setAllSections((allSections) => {
        const newSections = arrayMove(allSections, active, over);

        const updatedSection = newSections.map((section, index) => {
          return {
            ...section,
            sectionNumber: index + 1,
          };
        });
        console.log("updatedSection", updatedSection);

        return updatedSection;
      });
    }
  }

  const location = useLocation();
  const [initialValues, setInitialValues] = useState({
    curriculum: [],
  });
  useEffect(() => {
    setInitialValues(() => {
      return { curriculum: savedCourse.sections };
    });
  }, [savedCourse, location]);

  const isCurriculumEmpty = (curriculum) => {
    if (curriculum.length < 1) {
      return true;
    }
    return false;
  };

  useEffect(() => {
    setShowSectionModal(editMode);
  }, []);

  const handleAddSection = () => {
    setShowSectionModal(true);
  };

  const saveSectionIntoDb = async (sectionTitle, sectionNumber) => {
    setIsLoading(true);
    const payload = {
      authorEmail: courseDetails.authorEmail,
      authorUserId: cookies.userId,
      courseId: courseId || savedCourse.courseId,
      sectionNumber: sectionNumber,
      sectionTitle: sectionTitle,
      creationType: "sectionDetails",
    };

    return new Promise(async (resolve, reject) => {
      const response = await createCourseDetails(payload);
      if (response.status === 200) {
        setSectionId(response.data?.sectionId);
        const sectionData = {
          sectionNumber: sectionNumber,
          sectionTitle: sectionTitle,
          sectionId: response.data?.sectionId,
        };
        setAllSections((pre) => {
          return [...pre, sectionData];
        });
        setIsLoading(false);
        setShowSectionModal(false);
        return resolve(true);
      } else {
        return reject();
      }
    });
  };

  const putContentsInRespectiveSection = () => {
    console.log("allContent", allContent, allSections);

    const sections = [...allSections];
    const contents = [...allContent];

    if (contents.length > 0) {
      sections.forEach((section, index) => {
        contents.forEach((content, ind) => {
          // assign temp id to unique content
          contents[ind].tempId = Math.random();
          if (section?.sectionId === content?.sectionId) {
            sections[index] = {
              ...section,
              content: section?.content
                ? _.uniqBy([...section?.content, content], "tempId")
                : [content],
            };
          }
        });
      });

      setAllSections([...sections]);
    }
  };

  console.log("allContent", allContent, allSections);
  const putActiveSection = (sectionIndex, section) => {
    console.log("allSections", allSections, sectionIndex, section);

    const result = allSections.filter((section) => {
      return section.sectionNumber === sectionIndex + 1;
    });

    // if (result.length > 0) {
    //   setActiveSection(result[0]);
    // }
    setActiveSection(section);
  };

  useEffect(() => {
    putContentsInRespectiveSection();
  }, [allContent]);

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={(values) => {
        if (isCurriculumEmpty(values.curriculum)) {
          setCurriculumError("Minimum one curriculum is required");
        } else {
          setCurriculumError("");
          next();
        }
      }}
    >
      {({ values, setFieldValue, handleSubmit }) => {
        useEffect(() => {
          if (values.curriculum.length === 0) {
            values.curriculum = allSections;
          }
          if (editSavedCourse) {
            values.curriculum = savedCourse.sections;
          }
        }, [location]);

        return (
          <>
            <Form onSubmit={handleSubmit}>
              {showContentPage && allSections.length > 0 ? (
                <>
                  <AddLecture
                    sectionIndex={activeSectionIndex}
                    setShowContentPage={setShowContentPage}
                    allFiles={allFiles}
                    setAllFiles={setAllFiles}
                    respondSectionId={sectionId}
                    courseId={courseId}
                    allSections={allSections}
                    setAllSections={setAllSections}
                    allContent={allContent}
                    setAllContent={setAllContent}
                    activeSection={activeSection}
                    setActiveSection={setActiveSection}
                    activeContent={activeContent}
                    setActiveContent={setActiveContent}
                    activeQuiz={activeQuiz}
                    setActiveQuiz={setActiveQuiz}
                    editMode={editMode}
                    setEditMode={setEditMode}
                    setStep={setStep}
                    resetActiveQuiz={resetActiveQuiz}
                    editSavedCourse={editSavedCourse}
                    savedCourse={savedCourse}
                  />
                </>
              ) : (
                <>
                  <Card className="mb-3 border-0">
                    <Card.Header className="border-bottom px-4 py-3">
                      <h4 className="mb-0">Section</h4>
                    </Card.Header>
                    <Card.Body>
                      <FieldArray name="curriculum">
                        {({ push, remove }) => (
                          <Fragment>
                            <DndContext
                              sensors={sensors}
                              collisionDetection={closestCenter}
                              // adding !editSavedCourse for now, as the drag and drop should not work for saved course
                              onDragEnd={(e) =>
                                !editSavedCourse && handleSectionDragEnd(e)
                              }
                            >
                              <SortableContext
                                items={allSections.map(
                                  (item) => item.sectionNumber
                                )}
                                strategy={verticalListSortingStrategy}
                              >
                                {allSections.map((section, sectionIndex) => {
                                  return (
                                    <SortableItem
                                      key={section.sectionNumber}
                                      id={section.sectionNumber}
                                    >
                                      <Card key={sectionIndex} className="mt-3">
                                        <Card.Header className="">
                                          <div className="d-flex align-items-center justify-content-between">
                                            <h4 className="mb-0">
                                              {section?.sectionTitle}
                                            </h4>
                                            <Button
                                              variant="outline-primary"
                                              className="btn btn-outline-primary btn-sm "
                                              style={{
                                                border: "none",
                                                background: "#F2F6FC",
                                                color: "#224D99",
                                              }}
                                              disabled={!section?.sectionId}
                                              onClick={() => {
                                                setActiveSectionIndex(
                                                  sectionIndex
                                                );
                                                putActiveSection(
                                                  sectionIndex,
                                                  section
                                                );

                                                setShowContentPage(true);
                                              }}
                                            >
                                              + Add Content
                                            </Button>
                                          </div>

                                          {/* <div>
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  style={{ border: "none" }}
                                  onClick={() => {}}
                                >
                                  <i className="bi bi-pencil"></i>
                                </Button>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  style={{ border: "none" }}
                                  onClick={() => {
                                    dispatch(removeSection(sectionIndex));
                                    remove(sectionIndex);
                                  }}
                                >
                                  <i className="bi bi-trash3"></i>
                                </Button>
                              </div> */}
                                        </Card.Header>
                                        {allSections[sectionIndex]?.content && (
                                          <DndContext
                                            sensors={sensors}
                                            collisionDetection={closestCenter}
                                            onDragEnd={(e) =>
                                              handleDragEnd(
                                                e,
                                                allSections[sectionIndex],
                                                sectionIndex
                                              )
                                            }
                                          >
                                            <SortableContext
                                              items={allSections[
                                                sectionIndex
                                              ]?.content.map(
                                                (content) =>
                                                  content.contentNumber
                                              )}
                                              strategy={
                                                verticalListSortingStrategy
                                              }
                                            >
                                              {allSections[
                                                sectionIndex
                                              ]?.content?.map(
                                                (content, contentIndex) => (
                                                  <SortableItem
                                                    key={contentIndex}
                                                    id={content.contentNumber}
                                                  >
                                                    <div className="px-3 py-2 d-flex justify-content-between align-items-center">
                                                      <RenderContent
                                                        key={contentIndex}
                                                        fileName={
                                                          content?.contentFileName ||
                                                          ""
                                                        }
                                                        contentTitle={
                                                          content?.contentTitle ||
                                                          content?.title
                                                        }
                                                      />
                                                      <LucideGripVertical
                                                        size={16}
                                                      />
                                                    </div>
                                                  </SortableItem>
                                                )
                                              )}
                                            </SortableContext>
                                          </DndContext>
                                        )}
                                      </Card>
                                    </SortableItem>
                                  );
                                })}
                              </SortableContext>
                            </DndContext>
                            <div className="text-danger mt-2">
                              {curriculumError && curriculumError}
                            </div>
                            <AddSectionModal
                              show={showSectionModal}
                              setStep={setStep}
                              handleClose={() => {
                                setShowSectionModal(false);
                                if (editMode) {
                                  updateEditMode(false);
                                }
                              }}
                              editMode={editMode}
                              updateEditMode={updateEditMode}
                              isLoading={isLoading}
                              addSection={async () => {
                                await saveSectionIntoDb(
                                  sectionTitle,
                                  values.curriculum.length + 1
                                );

                                push({
                                  sectionTitle: sectionTitle,
                                  lectures: [],
                                });
                                setSectionTitle("");
                              }}
                              courseDetails={courseDetails}
                              setCourseDetails={setCourseDetails}
                              courseId={courseId}
                              setCourseId={setCourseId}
                              activeSection={activeSection}
                              allSections={allSections}
                              setAllSections={setAllSections}
                              sectionTitle={sectionTitle}
                              setSectionTitle={setSectionTitle}
                            />
                          </Fragment>
                        )}
                      </FieldArray>
                    </Card.Body>
                  </Card>
                  {/* {!editSavedCourse ? (
                    <> */}
                  <Button
                    type="button"
                    class="btn btn-outline-primary bg-transparent text-primary w-100"
                    onClick={handleAddSection}
                  >
                    + Add Section
                  </Button>
                  {/* </>
                  ) : null} */}

                  <div className="d-flex justify-content-between mt-5">
                    <Button variant="secondary" onClick={previous}>
                      Previous
                    </Button>
                    <Button variant="primary" type="submit">
                      Next
                    </Button>
                  </div>

                  {activeSectionIndex !== null && (
                    <AddLectureModal
                      show={showLectureModal}
                      handleClose={() => setShowLectureModal(false)}
                      addLecture={(lectureTitle) => {
                        const updatedLectures = [
                          ...values.curriculum[activeSectionIndex].lectures,
                          lectureTitle,
                        ];

                        setFieldValue(
                          `curriculum[${activeSectionIndex}].lectures`,
                          updatedLectures
                        );
                        const payload = {
                          sectionIndex: activeSectionIndex,
                          content: updatedLectures,
                        };
                      }}
                    />
                  )}
                </>
              )}
            </Form>
          </>
        );
      }}
    </Formik>
  );
};

export default Curriculum;
